import classNames from 'classnames';
import * as React from 'react';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import { HtmlHeading } from '~/Partials/Controls/HtmlHeading';
import { Link } from '~/Partials/Link/Link';
import { LinkType } from '~/Partials/Link/LinkType.csharp';
import type { LinkViewModel } from '~/Partials/Link/LinkViewModel.csharp';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { useClickProxyAllowingTextSelection } from '~/Utils/Hooks/useClickProxyAllowingTextSelection';
import type { Theme } from '~/Utils/Theme';
import styles from './Card.module.css';
import type { CardColorVariant } from './CardColorVariant';

export interface CardProps {
    link: LinkViewModel;
    heading?: HtmlString;
    content?: HtmlString;
    theme?: Pick<Theme, 'withRoundedCorners'>;
    colorVariant?: CardColorVariant;
    className?: string;
    contentClassName?: string;
}

export const Card: React.FC<CardProps> = props => {
    const contextualTheme = useTheme();
    const {
        heading,
        content,
        link,
        className,
        contentClassName,
        theme = contextualTheme,
        colorVariant = 'light',
    } = props;

    const { clickableRef, onProxyMouseDown, onProxyMouseUp } = useClickProxyAllowingTextSelection<HTMLAnchorElement>();

    const { withRoundedCorners } = theme;
    const richtextVariant = colorVariant === 'light' ? 'dark' : 'light';

    return (
        <div
            role="none"
            onMouseDown={onProxyMouseDown}
            onMouseUp={onProxyMouseUp}
            tabIndex={-1}
            className={classNames(
                styles.Card,
                styles[`Variant--${colorVariant}`],
                withRoundedCorners && styles['Variant--rounded'],
                className
            )}
        >
            {heading && <HtmlHeading type="h3" html={heading} className={styles.Heading} />}
            {content && (
                <HtmlDiv
                    className={classNames(
                        styles.Content,
                        'RichtextContent',
                        `RichtextContent--${richtextVariant}`,
                        contentClassName
                    )}
                    html={content}
                />
            )}
            <Link {...link} showIsExternal={false} ref={clickableRef} type={LinkType.Clear} className={styles.Link}>
                <span className="visuallyHidden">{link.label}</span>
                <SvgIcon iconName="arrow-right-circle" className={styles.LinkIcon} />
            </Link>
        </div>
    );
};
